import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Webhook,
  Add,
  Edit,
  Delete,
  Refresh,
  PlayArrow,
  History,
  CheckCircle,
  Error,
  Schedule,
  ContentCopy,
  Close,
  Info,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../../services/api';

interface WebhookData {
  idWebhook: number;
  url: string;
  email?: string;
  codigoTipoMovimento: number;
  descricaoTipoMovimento: string;
  codigoPeriodoMovimento: number;
  descricaoPeriodoMovimento: string;
  codigoSituacao: number;
  descricaoSituacao: string;
  dataHoraCadastro: string;
  dataHoraUltimaAlteracao?: string;
  dataHoraInativacao?: string;
  descricaoMotivoInativacao?: string;
}

interface WebhookSolicitacao {
  codigoWebhookSituacao: number;
  descricaoWebhookSituacao: string;
  codigoSolicitacaoSituacao: number;
  descricaoSolicitacaoSituacao: string;
  codigoTipoMovimento: number;
  descricaoTipoMovimento: string;
  descricaoErroProcessamento?: string;
  dataHoraCadastro: string;
  nossoNumero?: number;
  codigoBarras?: string;
  webhookNotificacoes?: {
    url: string;
    dataHoraInicio: string;
    dataHoraFim: string;
    codigoStatusRequisicao: number;
    descricaoCodigoStatusRequisicao: string;
  }[];
}

const TIPO_MOVIMENTO = [
  { value: 7, label: 'Pagamento (Baixa operacional)' },
];

const PERIODO_MOVIMENTO = [
  { value: 1, label: 'Movimento atual (D0)' },
];

const WebhooksSettingsPage: React.FC = () => {
  const [webhooks, setWebhooks] = useState<WebhookData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Form states
  const [formUrl, setFormUrl] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formTipoMovimento, setFormTipoMovimento] = useState(7);
  const [formPeriodoMovimento, setFormPeriodoMovimento] = useState(1);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookData | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // History
  const [solicitacoes, setSolicitacoes] = useState<WebhookSolicitacao[]>([]);
  const [historyDate, setHistoryDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [historyLoading, setHistoryLoading] = useState(false);

  // Get webhook receiver URL for display
  const getReceiverUrl = () => {
    const baseUrl = window.location.origin.replace(':5173', ':3333');
    return `${baseUrl}/api/v1/boletos/webhooks/receiver`;
  };

  const fetchWebhooks = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/boletos/webhooks');
      if (response.data.success) {
        const data = response.data.data?.resultado || response.data.data || [];
        setWebhooks(Array.isArray(data) ? data : [data]);
      }
    } catch (err: any) {
      console.error('Error fetching webhooks:', err);
      if (err.response?.status !== 204) {
        setError(err.response?.data?.error?.message || 'Erro ao carregar webhooks');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const handleAdd = async () => {
    if (!formUrl) {
      setError('URL é obrigatória');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/boletos/webhooks', {
        url: formUrl,
        email: formEmail || undefined,
        codigoTipoMovimento: formTipoMovimento,
        codigoPeriodoMovimento: formPeriodoMovimento,
      });

      if (response.data.success) {
        setSuccess('Webhook cadastrado com sucesso');
        setAddDialogOpen(false);
        resetForm();
        fetchWebhooks();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao cadastrar webhook');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedWebhook) return;

    setSubmitting(true);
    try {
      await api.patch(`/boletos/webhooks/${selectedWebhook.idWebhook}`, {
        url: formUrl,
        email: formEmail || undefined,
      });

      setSuccess('Webhook atualizado com sucesso');
      setEditDialogOpen(false);
      resetForm();
      fetchWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao atualizar webhook');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedWebhook) return;

    setSubmitting(true);
    try {
      await api.delete(`/boletos/webhooks/${selectedWebhook.idWebhook}`);

      setSuccess('Webhook excluído com sucesso');
      setDeleteConfirmOpen(false);
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao excluir webhook');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async (webhook: WebhookData) => {
    try {
      await api.patch(`/boletos/webhooks/${webhook.idWebhook}/reativar`);
      setSuccess('Webhook reativado com sucesso');
      fetchWebhooks();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Erro ao reativar webhook');
    }
  };

  const handleViewHistory = async (webhook: WebhookData) => {
    setSelectedWebhook(webhook);
    setHistoryDialogOpen(true);
    loadHistory(webhook.idWebhook, historyDate);
  };

  const loadHistory = async (idWebhook: number, date: string) => {
    setHistoryLoading(true);
    try {
      const response = await api.get(`/boletos/webhooks/${idWebhook}/solicitacoes`, {
        params: { dataSolicitacao: date },
      });

      if (response.data.success) {
        const data = response.data.data?.resultado?.webhookSolicitacoes || [];
        setSolicitacoes(data);
      }
    } catch (err: any) {
      if (err.response?.status !== 204) {
        console.error('Error loading history:', err);
      }
      setSolicitacoes([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const resetForm = () => {
    setFormUrl('');
    setFormEmail('');
    setFormTipoMovimento(7);
    setFormPeriodoMovimento(1);
    setSelectedWebhook(null);
  };

  const openEditDialog = (webhook: WebhookData) => {
    setSelectedWebhook(webhook);
    setFormUrl(webhook.url);
    setFormEmail(webhook.email || '');
    setEditDialogOpen(true);
  };

  const openDeleteConfirm = (webhook: WebhookData) => {
    setSelectedWebhook(webhook);
    setDeleteConfirmOpen(true);
  };

  const getStatusChip = (situacao: number, descricao: string) => {
    switch (situacao) {
      case 1:
        return <Chip icon={<Schedule />} label={descricao} color="warning" size="small" />;
      case 2:
        return <Chip icon={<Error />} label={descricao} color="error" size="small" />;
      case 3:
        return <Chip icon={<CheckCircle />} label={descricao} color="success" size="small" />;
      case 4:
        return <Chip icon={<Error />} label={descricao} color="default" size="small" />;
      default:
        return <Chip label={descricao} size="small" />;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copiado para a área de transferência');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <Webhook sx={{ mr: 1, verticalAlign: 'middle' }} />
          Configuração de Webhooks
        </Typography>
        <Box>
          <Button
            startIcon={<Refresh />}
            onClick={fetchWebhooks}
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Atualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              resetForm();
              setAddDialogOpen(true);
            }}
          >
            Novo Webhook
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Info card */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'info.light', borderColor: 'info.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Info color="info" />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" color="info.dark">
                URL do Receptor de Webhooks
              </Typography>
              <Typography variant="body2" color="info.dark" sx={{ mb: 1 }}>
                Configure esta URL no Sicoob para receber notificações de pagamentos em tempo real:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{
                    bgcolor: 'white',
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    flex: 1,
                  }}
                >
                  {getReceiverUrl()}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => copyToClipboard(getReceiverUrl())}
                  color="info"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Webhooks table */}
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : webhooks.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell>ID</TableCell>
                  <TableCell>URL</TableCell>
                  <TableCell>Tipo Movimento</TableCell>
                  <TableCell>Período</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cadastro</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {webhooks.map((webhook) => (
                  <TableRow key={webhook.idWebhook} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {webhook.idWebhook}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={webhook.url}
                      >
                        {webhook.url}
                      </Typography>
                      {webhook.email && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {webhook.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{webhook.descricaoTipoMovimento}</TableCell>
                    <TableCell>{webhook.descricaoPeriodoMovimento}</TableCell>
                    <TableCell>
                      {getStatusChip(webhook.codigoSituacao, webhook.descricaoSituacao)}
                      {webhook.descricaoMotivoInativacao && (
                        <Typography variant="caption" color="error" display="block">
                          {webhook.descricaoMotivoInativacao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {webhook.dataHoraCadastro &&
                        format(parseISO(webhook.dataHoraCadastro), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver histórico">
                        <IconButton size="small" onClick={() => handleViewHistory(webhook)}>
                          <History fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEditDialog(webhook)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {webhook.codigoSituacao === 4 && (
                        <Tooltip title="Reativar">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleReactivate(webhook)}
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteConfirm(webhook)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Webhook sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography color="text.secondary">
              Nenhum webhook configurado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure um webhook para receber notificações de pagamentos de boletos
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
            >
              Cadastrar Webhook
            </Button>
          </Box>
        )}
      </Paper>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cadastrar Webhook</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL do Webhook *"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
                placeholder="https://seu-dominio.com/webhook"
                helperText="URL que receberá as notificações do Sicoob"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-mail de Notificação"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                placeholder="email@dominio.com"
                helperText="E-mail para receber alertas em caso de erro"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Movimento</InputLabel>
                <Select
                  value={formTipoMovimento}
                  label="Tipo de Movimento"
                  onChange={(e) => setFormTipoMovimento(Number(e.target.value))}
                >
                  {TIPO_MOVIMENTO.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Período</InputLabel>
                <Select
                  value={formPeriodoMovimento}
                  label="Período"
                  onChange={(e) => setFormPeriodoMovimento(Number(e.target.value))}
                >
                  {PERIODO_MOVIMENTO.map((periodo) => (
                    <MenuItem key={periodo.value} value={periodo.value}>
                      {periodo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={submitting || !formUrl}
          >
            {submitting ? <CircularProgress size={20} /> : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Webhook</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            Ao alterar a URL, o webhook entrará em estado "Aguardando validação" até que a nova URL seja validada.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="URL do Webhook *"
                value={formUrl}
                onChange={(e) => setFormUrl(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="E-mail de Notificação"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEdit}
            disabled={submitting || !formUrl}
          >
            {submitting ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta ação não pode ser desfeita!
          </Alert>
          <Typography>
            Deseja realmente excluir o webhook <strong>#{selectedWebhook?.idWebhook}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            URL: {selectedWebhook?.url}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              <History sx={{ mr: 1, verticalAlign: 'middle' }} />
              Histórico de Notificações
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Webhook #{selectedWebhook?.idWebhook}
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryDialogOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              type="date"
              label="Data"
              value={historyDate}
              onChange={(e) => setHistoryDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="outlined"
              onClick={() => selectedWebhook && loadHistory(selectedWebhook.idWebhook, historyDate)}
              disabled={historyLoading}
            >
              {historyLoading ? <CircularProgress size={20} /> : 'Buscar'}
            </Button>
          </Box>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : solicitacoes.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>Data/Hora</TableCell>
                    <TableCell>Nosso Número</TableCell>
                    <TableCell>Status Webhook</TableCell>
                    <TableCell>Status Envio</TableCell>
                    <TableCell>Código HTTP</TableCell>
                    <TableCell>Erro</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {solicitacoes.map((sol, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>
                        {sol.dataHoraCadastro &&
                          format(parseISO(sol.dataHoraCadastro), 'dd/MM/yyyy HH:mm:ss')}
                      </TableCell>
                      <TableCell>{sol.nossoNumero || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={sol.descricaoWebhookSituacao}
                          size="small"
                          color={sol.codigoWebhookSituacao === 3 ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={sol.descricaoSolicitacaoSituacao}
                          size="small"
                          color={
                            sol.codigoSolicitacaoSituacao === 3
                              ? 'success'
                              : sol.codigoSolicitacaoSituacao === 6
                              ? 'error'
                              : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {sol.webhookNotificacoes?.[0]?.codigoStatusRequisicao || '-'}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {sol.descricaoErroProcessamento || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <History sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">
                Nenhuma notificação encontrada para esta data
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WebhooksSettingsPage;
